import { useQuery } from "@tanstack/react-query";
import { navigate } from "gatsby";
import { parsePhoneNumber } from "react-phone-number-input";
import { getCookie, setCookie } from "react-use-cookie";
import { v4 as uuidv4 } from "uuid";
import { JobQueryArray } from "../../gatsby-node";
import { SERPConfig } from "../../plugins/gatsby-source-serp/types";
import { FormValues } from "../templates/serp/Serp";
import getCountryCodeFromCountry from "../utils/getCountryCodeFromCountry";
import { Source } from "./useJobsQuery";

const DOMAIN = process.env.GATSBY_DOMAIN;

const countryCode = getCountryCodeFromCountry();

type CookieValues = {
  email?: string;
  firstname?: string;
  lastname?: string;
  phone?: string;
  compare?: boolean;
};

export type AllowedSearchParams = CookieValues & {
  limit?: number;
  location: string;
  offset?: number;
  position: string;
  source?: Source.path | Source.serp;
  sources: JobQueryArray;
  positionOverride?: SERPConfig["positionOverride"];
  start?: string;
  edu_optin: boolean; // TODO - should be strings?
  email_optin: boolean;
  sms_optin: boolean;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: FormValues["utm_source"];
  utm_term?: string;
  qrn_campaign?: string;
  qrn_content?: string;
  qrn_medium?: string;
  qrn_source?: string;
  qrn_term?: string;
  gclid?: string;
  jobhash?: string;
  jobtitle?: string;
  company?: string;
  jobid?: string;
  redirect?: string;
  locphysical?: string;
};

const SEARCH_PARAMS_ALLOWLIST = [
  "location",
  "position",
  "start",

  "utm_campaign",
  "utm_content",
  "utm_medium",
  "utm_source",
  "utm_term",

  // these override corresponding utm_*
  "qrn_campaign",
  "qrn_content",
  "qrn_medium",
  "qrn_source",
  "qrn_term",

  "lscid",
  "clickid",
  "gclid",
  "locphysical",

  // used in /optout
  "mail_id",
  "mail_token",

  // These should probably be in cookies instead of URL. They don't affect the query.
  "email_optin",
  "sms_optin",
  "edu_optin",
  "email",
  "phone",
  "compare",
  "firstname",
  "lastname",

  // RG Direct values
  "jobhash",
  "jobtitle",
  "company",

  "jobid",
  "redirect",
];

// TODO - remove?
function convertBooleanStringsToBooleans(value: string): boolean | string {
  switch (value.toLowerCase()) {
    case "false":
    case "0":
      return false;
    case "true":
    case "1":
      return true;
    default:
      return value;
  }
}

// https://api.best-jobs-online.com/geotarget-lookup?id=1000002
// => {"Criteria ID":1000002,"Name":"Kabul","Canonical Name":"Kabul,Kabul,Afghanistan","Parent ID":9075393,"Country Code":"AF","Target Type":"City","Status":"Active"}
// TODO
// Memoise as this can be called multiple times
async function adWordsGeoTargetLookup(locphysical?: string) {
  try {
    const response = await fetch(
      `https://api-v2.${DOMAIN}/geotarget-lookup?id=${locphysical}`
    );
    if (response.ok) {
      const { Name } = await response.json();
      return Name;
    }
  } catch (error) {
    console.error(error);
  }
}

function getUniqueId() {
  const uniqueId = getCookie("uniqueId");
  if (uniqueId) {
    return uniqueId;
  }

  const newUniqueId = uuidv4();
  setCookie("uniqueId", newUniqueId, {
    days: 0.1, // 2.4 hours
  });

  return newUniqueId;
}

export async function sniffLocation() {
  try {
    const response = await fetch(
      `https://api-v2.${DOMAIN}/whif?uuid=${getUniqueId()}`
    );
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error(error);
    return {};
  }
}

export async function sanitiseSearchParams(
  urlSearchParams: URLSearchParams
): Promise<AllowedSearchParams> {
  let searchParams: AllowedSearchParams = {};
  for (let [key, value] of urlSearchParams) {
    key = key.toLowerCase();
    if (!SEARCH_PARAMS_ALLOWLIST.includes(key)) continue;

    if (value && key === "phone") {
      const phoneNumber = parsePhoneNumber(value, countryCode);
      if (phoneNumber) {
        value = phoneNumber.number; // this includes the country code, which isn't always wanted - eg by DTL
      }
    }

    searchParams[key] = convertBooleanStringsToBooleans(value);

    if (key === "locphysical" && value && !searchParams.location) {
      const locphysicalValue = await adWordsGeoTargetLookup(
        searchParams.locphysical as string
      );
      if (locphysicalValue) {
        searchParams.location = locphysicalValue;
      }
    }
  }

  if (!searchParams.location) {
    // there's no `location` or `locphysical` in the url, so let's sniff it
    const sniffedLocation = await sniffLocation();
    if (sniffedLocation?.cityName) {
      searchParams.location = sniffedLocation.cityName;
    }
  }

  return searchParams;
}

function getPIICookieValues() {
  const email = getCookie("reg:email") || getCookie("email");
  const firstname = getCookie("firstname");
  const lastname = getCookie("lastname");
  const phone = getCookie("reg:phone") || getCookie("phone");
  const compare = getCookie("compare");

  let cookieValues: CookieValues = {};
  if (email) cookieValues.email = email;
  if (firstname) cookieValues.firstname = firstname;
  if (lastname) cookieValues.lastname = lastname;
  if (phone) cookieValues.phone = phone;
  if (compare === "") {
    cookieValues.compare = true; // default true if unset
  } else {
    cookieValues.compare = convertBooleanStringsToBooleans(compare) as boolean;
  }

  return cookieValues;
}
const cookieOptions = {
  days: 30,
  path: "/",
};

function storePIIInCookies(searchParams: AllowedSearchParams) {
  const {
    email,
    firstname,
    lastname,
    phone,
    compare,
    ...searchParamsWithoutPII
  } = searchParams;
  // set cookies for PII
  if (email) setCookie("email", email, cookieOptions);
  if (firstname) setCookie("firstname", firstname, cookieOptions);
  if (lastname) setCookie("lastname", lastname, cookieOptions);
  if (phone) setCookie("phone", phone, cookieOptions);

  return searchParamsWithoutPII;
}

async function getFormValues(search: Location["search"]) {
  const urlSearchParams = new URLSearchParams(search);

  const qrnUserId = urlSearchParams.get("qrnUserId");
  if (qrnUserId) {
    setCookie("reg:smsId", qrnUserId, { path: "/", days: 365 });
    // remove it from the url in attempt to prevent other people using the same qrnUserId
    urlSearchParams.delete("qrnUserId");
    navigate(window.location.pathname + "?" + urlSearchParams.toString(), {
      replace: true,
    });
    return {};
  }

  const searchParams = await sanitiseSearchParams(urlSearchParams);
  const searchParamsWithoutPII = storePIIInCookies(searchParams);
  const cookieValues = getPIICookieValues();
  return Object.assign(searchParamsWithoutPII, cookieValues);
}

export default function useFormValuesFromURL(
  location: Location,
  onSuccess?: (Args: AllowedSearchParams) => void
) {
  const { search } = location;
  const queryKey = ["querystring:" + search];
  const response = useQuery(queryKey, () => getFormValues(search), {
    onSuccess,
    // these ensure fresh results on back/forward navigation
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: "always",
  });
  return response;
}
